/**
 * Batch 31a.2.1 — POST /auth/login dual-shape response types.
 *
 * Per ``SAAS_ARCHITECTURE.md`` MFA section (post-31a.2): /auth/login on
 * either host returns one of two shapes based on the opt-in policy:
 *
 *   - Direct mint (default — SA without active credential, OR tenant
 *     with mfa_required=false):
 *       { access_token, token_type, expires_in }
 *     plus refresh cookie set on the response.
 *
 *   - MFA pending (SA with active credential, OR tenant with
 *     mfa_required=true):
 *       { mfa_pending_token, expires_in,
 *         mfa_enrollment_required: bool,
 *         next_step: "enroll" | "verify" }
 *     no refresh cookie at this stage.
 *
 * Discriminator: presence of ``access_token`` vs ``mfa_pending_token``.
 * Both ``useAdminLogin`` and ``useLogin`` (tenant) consume this union.
 */

type DirectLoginResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};

type MfaPendingLoginResponse = {
  mfa_pending_token: string;
  expires_in: number;
  mfa_enrollment_required: boolean;
  next_step: "enroll" | "verify";
  // Email-MFA B2/B3 — present ONLY on the tenant email-code pending shape
  // (``next_step`` is hard-set to "verify", ``mfa_enrollment_required`` to
  // false). Its presence is the discriminator the FE uses to render the
  // email-code challenge variant instead of the TOTP one; the boolean value
  // says whether the code email actually went out (false ⇒ offer "resend").
  code_email_sent?: boolean;
  // WA-1b — which MFA methods the user has enrolled. Subset of
  // ``["totp", "webauthn"]``. Backend addendum may add this field after the
  // WA-1b FE ships; the frontend degrades gracefully when absent/empty.
  available_methods?: string[];
};

export type LoginResponse = DirectLoginResponse | MfaPendingLoginResponse;

export function isMfaPendingResponse(
  resp: LoginResponse,
): resp is MfaPendingLoginResponse {
  return "mfa_pending_token" in resp;
}

/**
 * Email-code discriminator: the tenant email-code login path is the only
 * one that returns ``code_email_sent``. TOTP / SA pending responses omit it.
 * Used to pick the email-code challenge variant on the verify screen.
 */
export function isEmailCodePending(
  resp: MfaPendingLoginResponse,
): boolean {
  return "code_email_sent" in resp;
}
